import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { request, setUnauthorizedHandler } from "../api/http";

const STORAGE_KEY = "admin:token";

type LoginResponse = { token: string };

type AuthContextValue = {
  token: string | null;
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => void;
  // True when the last logout was triggered by a global 401 (session
  // expired) rather than an explicit user action. The Login view reads
  // this to render a one-line notice and calls clearSessionExpired() on
  // a subsequent successful login or first user interaction.
  sessionExpired: boolean;
  clearSessionExpired: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY),
  );
  const [sessionExpired, setSessionExpired] = useState(false);

  const login = useCallback(
    async (identifier: string, password: string) => {
      const data = await request<LoginResponse>("/api/admin/auth/login", {
        method: "POST",
        body: { username: identifier, password },
      });
      localStorage.setItem(STORAGE_KEY, data.token);
      setToken(data.token);
      setSessionExpired(false);
    },
    [],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
  }, []);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  useEffect(() => {
    // Register the global 401 handler at the http layer. When a request
    // returns 401, this clears the token (triggers ProtectedRoute to
    // navigate to /login on the next render) and surfaces a flag the
    // Login view can use to explain the auto-redirect.
    setUnauthorizedHandler(() => {
      // Guard so an unauthenticated burst (e.g. Login page firing
      // background requests) doesn't set the "expired" notice — only
      // surface it when there actually WAS a session to lose.
      if (null !== localStorage.getItem(STORAGE_KEY)) {
        setSessionExpired(true);
      }
      localStorage.removeItem(STORAGE_KEY);
      setToken(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const value = useMemo(
    () => ({ token, login, logout, sessionExpired, clearSessionExpired }),
    [token, login, logout, sessionExpired, clearSessionExpired],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return ctx;
}
